import React, { useState, useEffect } from 'react';
import useLocalStorage from '../hooks/useLocalStorage';
import GenericSection from '../components/sections/GenericSection';
import SectionHeader from '../components/sections/partials/SectionHeader';
import B2BProductList from '../components/sections/B2BProductList';
import Input from '../components/elements/Input';
import Button from '../components/elements/Button';
import { Link } from 'react-router-dom';

const TradeDivision = () => {
  const [quoteCart, setQuoteCart] = useLocalStorage('sees_quote_cart', []);
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch('/api/hubspot-products');
        if (!response.ok) throw new Error('Network error');
        const data = await response.json();
        if (Array.isArray(data)) {
          setProducts(data);
        } else {
          setProducts([]);
        }
      } catch (error) {
        console.error('Failed to load trade products:', error);
        setProducts([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const filteredProducts = products.filter(p => 
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    (p.description && p.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleAddToQuote = (product) => {
    const cartItem = { ...product, cartId: Date.now() + Math.random().toString(36).substr(2, 9), quantity: 1 };
    setQuoteCart([...quoteCart, cartItem]);
  };

  const tradeHeader = {
    title: 'Global Trade & Sourcing Group',
    paragraph: 'Operating a robust procurement and trade network connecting major manufacturing hubs in Asia with high-demand commercial markets across North America, Europe, the GCC, and Africa.',
  };

  const pillars = [
    {
      title: 'Global Reselling, Sourcing & Cross-Border Trade',
      bullets: [
        'Global Procurement Network: Direct sourcing of industrial materials, consumer goods, and raw commodities from fully vetted manufacturers.',
        'Multi-Regional Supply Chain Management: End-to-end import/export workflows, cross-border customs navigation, and international logistics.',
        'Strategic Gateway Operations: Key maritime ports in the UAE and North America as central staging and re-export hubs.'
      ]
    },
    {
      title: 'Fast-Moving Consumer Goods (FMCG) & Agricultural Trade',
      bullets: [
        'Confectionery & Gourmet Goods: Sourcing and global distribution of specialty foods, chocolates, and premium snacks.',
        'Spices & Agricultural Commodities: Bulk import, export, and distribution of high-grade spices, cardamom, chili varieties, and herbs.',
        'Global Compliance & Quality Control: Full adherence to FDA, regional food safety standards, and international clearance.'
      ]
    },
    {
      title: 'Industrial Coatings, Paints & Chemical Export',
      bullets: [
        'Global & Regional Procurement: Sourcing premium-grade architectural, marine, and protective coatings from top global producers.',
        'Cross-Border Distribution: Direct-to-contractor and commercial wholesale supply of industrial paints, thinners, and base coats.',
        'Overland & Maritime Freight: Managing land-border exports into GCC markets alongside containerized ocean freight to Asia and Africa.'
      ]
    },
    {
      title: 'B2B Commercial Brokerage & Intermediary Trade',
      bullets: [
        'High-Value Deal Structuring: Connecting global buyers and suppliers across wholesale commodity markets to execute transactions.',
        'Agency & Commission Representation: Facilitating cross-border trade agreements without locking up client capital in warehousing.',
        'Risk-Shielded Sourcing: Providing secure, transparent contractual bridges for enterprise-level procurement.'
      ]
    }
  ];

  return (
    <>
      <GenericSection topOuterDivider pb={0}>
        <SectionHeader data={tradeHeader} className="center-content" />
      </GenericSection>

      {/* 4 Trade Pillars Overview (3 on Top Row, Pillar 04 Spanning Entire Bottom Row) */}
      <section className="container mb-48">
        {/* Top Row: Pillars 01, 02, 03 (3 Boxes Equally Spaced) */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '24px', marginBottom: '24px', alignItems: 'stretch' }}>
          {pillars.slice(0, 3).map((pillar, idx) => (
            <div key={idx} className="p-24" style={{ backgroundColor: '#141C2E', borderRadius: '8px', border: '1px solid #1E293B', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
              <div>
                <div style={{ color: '#A88756', fontWeight: '500', fontSize: '0.85rem', marginBottom: '8px' }}>Trade Pillar 0{idx + 1}</div>
                <h4 className="mt-0 mb-16" style={{ color: '#A88756', fontSize: '1.2rem' }}>{pillar.title}</h4>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {pillar.bullets.map((b, bIdx) => (
                    <li key={bIdx} className="mb-12 text-sm" style={{ color: '#CBD5E1', lineHeight: '1.6' }}>
                      • {b}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Row: Pillar 04 (Spreads Across Entire Row / All 3 Columns) */}
        {pillars.slice(3, 4).map((pillar, idx) => (
          <div key={idx} className="p-24" style={{ backgroundColor: '#141C2E', borderRadius: '8px', border: '1px solid #1E293B', width: '100%' }}>
            <div>
              <div style={{ color: '#A88756', fontWeight: '500', fontSize: '0.85rem', marginBottom: '8px' }}>Trade Pillar 04</div>
              <h4 className="mt-0 mb-16" style={{ color: '#A88756', fontSize: '1.2rem' }}>{pillar.title}</h4>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {pillar.bullets.map((b, bIdx) => (
                  <li key={bIdx} className="mb-12 text-sm" style={{ color: '#CBD5E1', lineHeight: '1.6' }}>
                    • {b}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </section>

      {/* Product Search & Catalog Section */}
      <GenericSection topDivider>
        <div className="container" style={{ textAlign: 'center' }}>
          <h3 className="mt-0 mb-12" style={{ color: '#A88756' }}>Most Frequently Sourced Items</h3>
          <p className="text-sm text-color-high mb-24" style={{ maxWidth: '650px', margin: '0 auto 24px', color: '#CBD5E1' }}>
            Browse our primary catalog of commercial equipment, industrial supplies, and diagnostic items. For any custom items not in this list, we are happy to source them for you.
          </p>
          <div className="mb-24">
            <Link to="/contact">
              <Button color="primary" size="sm">Contact Us for Unlisted Items →</Button>
            </Link>
          </div>
        </div>

        <section className="container mb-32" style={{ maxWidth: '600px', margin: '0 auto' }}>
          <Input 
            id="trade-product-search"
            type="search"
            placeholder="Search trade catalog by item name or specification..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            hasIcon="right"
          />
        </section>

        {isLoading ? (
          <div className="text-center p-32">
            <p style={{ color: '#A88756' }}>Loading Product Catalog...</p>
          </div>
        ) : (
          <B2BProductList 
            products={filteredProducts} 
            onAddToQuote={handleAddToQuote}
          />
        )}

        <div className="text-center mt-32 mb-48">
          <Link to="/quote">
            <Button color="primary">Review Quote Basket ({quoteCart.length})</Button>
          </Link>
        </div>
      </GenericSection>
    </>
  );
};

export default TradeDivision;
